import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import {
  resetState,
  setIsTableLoading,
  setItems,
  setTotalItems,
} from "src/redux/slices/AffidavitCertificateUpdateSlice";
import { AppDispatch, RootState } from "src/redux/store";
import { formatDateAndTime } from "src/utils/dateAndTime";
import  AffidavitCertificate from "./AffidavitCertificate";
import { getColumns } from "src/utils/auth/getColumns";
import { useGetaffidavitCertificateDoneApplicationsQuery, useExportAffidavitCertificatesXlsxMutation } from "src/services/AffidavitCertificateService";
import SideNavLayout from "src/components/layouts/SideNavLayout/SideNavLayout";
import DownloadDialogWrapper from "./Dialog/DownloadDialogBox/DownloadDialogWrapper";
import { AffidavitCertificateUpdateListResponse } from "src/models/AffidavitCertificateUpdate.model";
import { Tooltip } from "@mui/material";
import { showToast } from "src/utils/toaster/showToast";
import moment from "moment";

const paramList = [
  "category",
  "name",
  "email",
  "adhaarNumber",
  "mobileNumber",
  "distributorCode",
  "appliedBy",
  "appliedAs",
  "txnId",
  "appliedFrom",
  "paymentCategory",
  "version",
  "panNumber",
  "acknowledgementNumber",
  "status",
  "assignedTo",
  "assignedBy",
];

const  AffidavitCertificateWrapper = () => {
  // Dispatch Hook
  const dispatch = useDispatch<AppDispatch>();
  // Slice
  const AffidavitCertificateDoneApplicationState: any = useSelector(
    (state: RootState) => state.AffidavitCertificateDoneApplication
  );
  const { items, searchValue, rowsPerPage, page, filterBy, dateFilter , orderByValue } =
  AffidavitCertificateDoneApplicationState;

  useEffect(() => {
    dispatch(resetState());
  }, []);


  const [isOpenDonwloadDialog, setIsOpenDownloadDialog] = useState(false);
  const [rowData, setRowData] = useState<AffidavitCertificateUpdateListResponse | null>(null);
  const [isUpdate, setIsUpdate] = useState(false);

  // Export XLSX
  const [exportXlsx, { isLoading: isExporting }] =
    useExportAffidavitCertificatesXlsxMutation();

  const handleExportXlsx = async () => {
    try {
      const response: any = await exportXlsx({
        limit: 0,
        searchValue: searchValue,
        params: paramList,
        page: 1,
        filterBy: filterBy,
        dateFilter: dateFilter,
        orderBy: "createdDateToShow",
        orderByValue: orderByValue,
        isPaginationRequired: false,
      });

      if (response?.data) {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Affidavit_Certificates_${moment().format("DD-MM-YYYY_HH-mm")}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        showToast("success", "XLSX exported successfully");
      }
    } catch (error) {
      console.error("Error exporting XLSX:", error);
      showToast("error", "Failed to export XLSX");
    }
  };

  // Get Data Query
  const { data, isFetching, isLoading } = useGetaffidavitCertificateDoneApplicationsQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: paramList,
    page: page,
    filterBy: filterBy,
    dateFilter: dateFilter,
    orderBy: "createdDateToShow",
    orderByValue: orderByValue,
    isPaginationRequired: true,
  });

  // Setting data
  React.useEffect(() => {

    if (!isFetching && !isLoading) {
      dispatch(setIsTableLoading(false));
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
    } else {
      dispatch(setIsTableLoading(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isFetching, data]);



  const columns: columnTypes[] = [
    {
      field: "createdDateToShow",
      headerName: "Date - Time",
      flex: "flex-[1_1_0%]",
      renderCell: (row: AffidavitCertificateUpdateListResponse) => (
        <div>
          <div className="text-slate-700 font-medium">
            {formatDateAndTime(row?.createdDateToShow, "DD MMM yyyy")}
          </div>
          <div className="text-[13px] font-medium text-slate-400">
            {formatDateAndTime(row?.createdDateToShow, "hh:mm A")}
          </div>
        </div>
      ),
    },
    {
      field: "srn",
      headerName: "SRN",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: AffidavitCertificateUpdateListResponse) => {
        return (
          <span className="text-primary-main text-[13px] text-ellipsis overflow-hidden">
            {" "}
            {row.srn}{" "}
          </span>
        );
      },
    },
    {
      field: "name",
      headerName: "Name",
      flex: "flex-[1_1_0%]",
      renderCell: (row: AffidavitCertificateUpdateListResponse) => (
        <span className="text-ellipsis overflow-hidden"> {row.name} </span>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: AffidavitCertificateUpdateListResponse) => {
        return (
          <span className="text-ellipsis overflow-hidden">
            {" "}
            {row.email || "N/A"}{" "}
          </span>
        );
      },
    },
    {
      field: "mobileNumber",
      headerName: "Mobile",
      flex: "flex-[1_1_0%]",
    },
    {
      noAuthRequired: true,
      field: "distributorCode",
      headerName: "SJBT  Code",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: AffidavitCertificateUpdateListResponse) => {
        return <span> {row.distributorCode || "N/A"} </span>;
      },
    },
    {
      noAuthRequired: true,
      field: "assignedTo",
      headerName: "Assignee",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: AffidavitCertificateUpdateListResponse) => {
        return (
          <div className="text-primary-main py-[10px] ">
            {row.assignedToName || "Not Assigned"}
          </div>
        );
      },
    },
    {
      field: "appliedByNumber",
      headerName: "Applied By (Mob. No.)",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: AffidavitCertificateUpdateListResponse) => {
        return <span> {row.appliedByNumber || "N/A"} </span>;
      },
    },
    {
      field: "acknowledgementNumber",
      headerName: "Acknowledgement Number",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: AffidavitCertificateUpdateListResponse) => {
        return (
          <Tooltip title={row.acknowledgementNumber}>
            <span className="text-primary-main text-[13px] text-ellipsis overflow-hidden">
               {row.acknowledgementNumber || "-"}
            </span>
          </Tooltip>
        );
      },
    },
      {
          noAuthRequired: true,
          field: "action",
          headerName: "Action",
          flex: "flex-[0_0_140px]",
          renderCell: (row: AffidavitCertificateUpdateListResponse) => {
            // Handle click action to open the dialog with row data
            const handleClick = () => {
              setRowData(row);
              setIsUpdate(false);
              setIsOpenDownloadDialog(true);
            };
        
            // Ensure file URL exists before allowing download
            const handleDownload = () => {
              if (row?.affidavitcertificatesData?.affidavitertificateFile) {
                downloadPdfFile(row.affidavitcertificatesData.affidavitertificateFile);
              } else if (row?.affidavitertificateFile) {
                downloadPdfFile(row.affidavitertificateFile);
              } else {
                console.error("File URL is missing.", row);
              }
            };

            // If the certificate status exists, render the "Download" button
            if (row?.affidavitcertificatesData?.status) {
              return (
                <div className="flex gap-3 items-center">
                  <span
                    className="text-green-500 font-medium flex gap-1 items-center cursor-pointer"
                    onClick={handleDownload}
                  >
                    Download
                  </span>
                  {/* Reopen with the saved details and generate again. */}
                  <span
                    className="text-blue-600 font-medium cursor-pointer"
                    onClick={() => {
                      setRowData(row);
                      setIsUpdate(true);
                      setIsOpenDownloadDialog(true);
                    }}
                  >
                    Update
                  </span>
                </div>
              );
            }
        
            // If status is "VERIFY", "GENERATE", or "DONE", render "Generate" button; otherwise, disable it
            const isGeneratable = ["VERIFY", "GENERATE", "DONE"].includes(row?.status);
            return (
              <button
                className={`py-1 px-3 rounded flex gap-2 items-center ${
                  isGeneratable
                    ? "bg-green-500 text-white hover:bg-green-600"
                    : "bg-gray-500 text-white cursor-not-allowed"
                }`}
                onClick={isGeneratable ? handleClick : undefined}
                disabled={!isGeneratable}
                title={!isGeneratable ? "Status is not VERIFY" : ""}
              >
                Generate
              </button>
            );
          },
    },
  ];


  
const downloadPdfFile = async (pdfUrl: string) => {
    try {
      const response = await fetch(pdfUrl, {
        method: 'GET',
      });
  
      if (!response.ok) {
        throw new Error('Failed to fetch the PDF file.');
      }
  
      const arrayBuffer = await response.arrayBuffer();
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
  
      const segments = pdfUrl.split(/[\/?#]/);
      const fileName = segments[segments.length - 1] || 'downloaded-file.pdf';
  
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };


  return (

    <>
      <SideNavLayout>

        {/* <>
        <FormControl fullWidth>
          <InputLabel id="demo-simple-select-label">Retailer</InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={agent}
            label="Age"
            onChange={handleChange}
          >
            <MenuItem value={10}>Ten</MenuItem>
            <MenuItem value={20}>Twenty</MenuItem>
            <MenuItem value={30}>Thirty</MenuItem>
          </Select>
        </FormControl>
        </> */}
        <AffidavitCertificate
          columns={getColumns(columns, "EMPLOYER_CERTIFICATE")}
          rows={items.map((item:any) => ({ ...item, key: item._id || item.srn }))}
          onExportXlsx={handleExportXlsx}
          isExporting={isExporting}
        />
      </SideNavLayout>
      {isOpenDonwloadDialog && (
        <DownloadDialogWrapper
          onClose={() => setIsOpenDownloadDialog(false)}
          businessEnquiryData={rowData}
          isUpdate={isUpdate}
        />
      )}
    </>
  );

};

export default AffidavitCertificateWrapper;
